import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import { useCallback, useEffect, useState, useContext } from 'react';
import abbreviateNumber from '@/home/utils/abbreviateNumber';
import { vexCdnUrl } from '~/constants/environment';
import { InventoryTypeId } from '~/constants/inventoryType';
import Store from '~/constants/store';
import { useIsLoggedIn } from '~/context/common/UserContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { useToggleFavorite } from '~/hooks/common/favorites';
import HeartWithBorder from '~/icons/HeartWithBorder';
import {
  FavoriteWrapper,
  Tile,
  TileImageSection,
  TileInfoSection,
  Trim,
  TrimMileage,
  YearMakeModel
} from './VehicleTile.styles';

const getInventoryType = (
  vehicleInventoryType: InventoryTypeId,
  storeKey = ''
) => {
  const isMarketplace =
    vehicleInventoryType === InventoryTypeId.DealerMarketplace &&
    storeKey === Store.DMP2Core.toLowerCase();
  const isManheim = storeKey === Store.Manheim.toLowerCase();
  const isLightAccident = storeKey === Store.LightAccident.toLowerCase();
  const shouldNotDisplayInventoryType = isLightAccident || isManheim;

  if (isMarketplace) {
    return 'Partner';
  } else if (shouldNotDisplayInventoryType) {
    return '';
  } else {
    return 'Carvana Certified';
  }
};

const VehicleTile = ({
  isFavorited,
  make,
  model,
  mileage = 0,
  price: { total },
  trim,
  year = 0,
  stockNumber,
  vehicleInventoryType,
  jellyBeanDesktopUrl,
  storeKey,
  vehicleId
}: RecommendedVehicle) => {
  const inventoryTypeText = getInventoryType(
    vehicleInventoryType,
    storeKey?.toLowerCase() ?? ''
  );
  const isWip = vehicleInventoryType === InventoryTypeId.WIP;
  const imageUrl = isWip
    ? jellyBeanDesktopUrl
    : `${vexCdnUrl}/${stockNumber}/standardizedHero.jpg`;
  const toggleFavorite = useToggleFavorite();
  const isLoggedIn = useIsLoggedIn();
  const [isFavoritedLocally, setFavoritedLocally] = useState(isFavorited);

  const { trackEvent } = useContext(AnalyticsContext);

  const handleFavorite = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      toggleFavorite({ vehicleId });

      if (isLoggedIn) setFavoritedLocally(v => !v);
      trackEvent('Ev Tiles favorites cta', { vehicleId });
    },
    [isLoggedIn, toggleFavorite, trackEvent, vehicleId]
  );

  useEffect(() => {
    setFavoritedLocally(isFavorited);
  }, [isFavorited]);

  return (
    <Tile>
      <TileImageSection>
        <Clickable
          data-analytics-id="Toggled Carousel Vehicle Fav"
          element={FavoriteWrapper}
          onClick={handleFavorite}
        >
          <HeartWithBorder
            blue
            height={20}
            width={20}
            isFavorited={isFavoritedLocally}
          />
        </Clickable>
        <picture>
          <source srcSet={imageUrl} media="(min-width: 1440px)" />
          <img
            className="h-full w-full rounded-t-md object-cover"
            src={imageUrl}
            alt={`${make} ${model}`}
          />
        </picture>
      </TileImageSection>
      <TileInfoSection>
        <div className="h-[18px] t-label-xs">{inventoryTypeText}</div>
        <YearMakeModel>
          {year} {make} {model}
        </YearMakeModel>
        <TrimMileage>
          <Trim>{trim} </Trim>
          <span className="mx-md inline-block h-4 w-4 rounded-full bg-text-default" />
          <span>{abbreviateNumber(mileage)} mi</span>
        </TrimMileage>
        <h3 className="t-strong-xl">${total.toLocaleString()}</h3>
      </TileInfoSection>
    </Tile>
  );
};

export default VehicleTile;
